import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import Highcharts from 'highcharts2/highstock'
import styled, { useTheme } from 'styled-components'
import i18n from 'live/services/i18n'
import { getActivityChartOptions } from './chart-options'
import {
    STATES_COLORS,
    renderButtons,
    StaticDataType,
    ButtonOptions,
    STATIC_DATA_STATES,
    renderCreationTooltip,
    DeleteConfirmationModal,
    StaticDataTypeI18nKey
} from '../chart-components'
import { ChartContainer, ChartTitle } from '../chart-components-style'
import { StaticDataModal } from '../modal-components'
import { deleteSingleEntity, TUBULAR_API } from '../../api/api'
import { getEmptyData } from '../../context/utils'
import { StaticDataContext } from '../../context/StaticDataContext'
import { ActivityEvent, RawActivityEvent, StaticDataSeries, TimeSpan, xRangeEvent } from '../../utils/type'
import { ActivityModal } from './ActivityModal'
import _ from 'lodash'
import { deleteAllTubularsFromRun } from '../../api/runApi'

require('highcharts/modules/xrange')(Highcharts)

const mapRawTubularTypeEvents = (
    data: RawActivityEvent[],
    chartTimeSpan: TimeSpan,
    parentSeries: StaticDataSeries
): ActivityEvent[] => {
    if (!data?.length) return []

    let emptyIntervals: ActivityEvent[] = []

    if (data.length > 1) {
        data.forEach((event, i) => {
            if (event?.end && data?.[i + 1]?.start && event.end < data[i + 1].start) {
                emptyIntervals = [...emptyIntervals, ...getEmptyData(event.end, data[i + 1].start, parentSeries)]
            }
        })
    }

    return [
        ...emptyIntervals,
        ...data?.map((event: RawActivityEvent) => getTubularTypeXRangeEvent(event, chartTimeSpan, parentSeries))
    ]
}

const getTubularTypeXRangeEvent = (
    event: any,
    chartTimeSpan?: TimeSpan,
    parentSeries?: StaticDataSeries
): xRangeEvent => {
    let end = event.end

    const parentData = parentSeries?.data?.findLast(p => p?.x && event?.start && p.x <= event.start) || null

    // if the event doesn't have end, try to get parent end
    if (!end) end = parentData?.x2
    if (!end) end = chartTimeSpan?.end

    return {
        name: event.name,
        color: event.end
            ? STATES_COLORS[STATIC_DATA_STATES.FINISHED]
            : event.id
            ? STATES_COLORS[STATIC_DATA_STATES.STARTED_IN_EXECUTION]
            : STATES_COLORS[STATIC_DATA_STATES.EMPTY],
        x: event?.start || end,
        x2: end,
        y: 0,
        isRealtime: !!parentData?.isRealtime,
        rawEvent: {
            ...event,
            state: !event?.end ? STATIC_DATA_STATES.STARTED_IN_EXECUTION : STATIC_DATA_STATES.FINISHED
        }
    }
}

export const getTubularSeries = (
    data: Array<RawActivityEvent>,
    chartTimeSpan: TimeSpan,
    parentSeries: StaticDataSeries,
    type: StaticDataType
): StaticDataSeries => {
    let xRangeSeriesData: Array<xRangeEvent> = []

    if (data && data.length) {
        let groupedData = []
        const tubularsPerDepthData = []
        const tubularsPerDepth = data?.filter(d => !d.start && !d.end)
        const groupedDepth = _.groupBy(tubularsPerDepth, 'run_id')

        Object.entries(groupedDepth).forEach(entry => {
            const runId = Number(entry[0])
            const tubulars = entry[1].sort((a, b) => {
                return a.id > b.id ? 1 : a.id < b.id ? -1 : 0
            })
            const runEvent = parentSeries.data.find(r => r.rawEvent?.id === runId)
            if (!runEvent) return
            const start = runEvent.rawEvent?.start
            const end = runEvent.x2
            let delta = 0
            const numberOfTubulars = tubulars.length
            delta = end ? (end - start) / numberOfTubulars : (chartTimeSpan.end - start) / numberOfTubulars
            tubulars.forEach((t, index) =>
                tubularsPerDepthData.push({ ...t, start: start + index * delta, end: start + (index + 1) * delta })
            )
        })
        groupedData = data
            .filter(d => d.start != null)
            .concat(tubularsPerDepthData)
            .sort((a, b) => {
                if (a.start && b.start) return a.start > b.start ? 1 : -1

                return -1
            })
        xRangeSeriesData = [
            ...getEmptyData(chartTimeSpan.begin, groupedData[0]?.start, parentSeries),
            ...mapRawTubularTypeEvents(groupedData, chartTimeSpan, parentSeries),
            ...getEmptyData(groupedData[groupedData.length - 1]?.end, chartTimeSpan.end, parentSeries)
        ]
    } else {
        xRangeSeriesData = [...getEmptyData(chartTimeSpan.begin, chartTimeSpan.end, parentSeries)]
    }

    return {
        name: type,
        type: 'xrange',
        data: xRangeSeriesData
    }
}
export const ActivityChart = (): ReactElement => {
    // Static Data Context
    const {
        buttonsRef,
        buttonController,
        setButtonController,
        currentChart,
        setAddEventButtonVisible,
        chartType,
        tooltipPosition,
        setTooltiPosition,
        widgetData,
        tryOpenModal,
        widgetTimeSpan,
        refresh,
        reloadData,
        checkBlockedTrackBeforeAction
    } = useContext(StaticDataContext)

    // Chart States
    const [highchartsElement, setHighchartsElement] = useState<Highcharts.Chart | null>(null)
    const [chartLoaded, setChartLoaded] = useState<boolean>(false)
    const [chartHeight, setChartHeight] = useState<number>(0)
    const chartRef = useRef<HTMLDivElement>(null)
    const theme = useTheme()

    // Modal States
    const [currentModalEvent, setCurrentModalEvent] = useState<RawActivityEvent>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [isModalDoubleSize, setIsModalDoubleSize] = useState<boolean>(false)
    const closeModal = (): void => setIsModalOpen(false)
    const openModal = (rawEvent: RawActivityEvent): void => {
        setCurrentModalEvent(rawEvent)
        setActivityTooltipMessage(null)
        setIsModalOpen(true)
    }

    // Delete Confirmation Modal States
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState<boolean>(false)
    const closeDeleteConfirmationModal = (): void => setIsDeleteConfirmationModalOpen(false)
    const openDeleteConfirmationModal = (rawEvent: RawActivityEvent): void => {
        setCurrentModalEvent(rawEvent)
        setIsDeleteConfirmationModalOpen(true)
    }

    const handleDelete = (isDeletingAllTubulars: boolean) => {
        ;(isDeletingAllTubulars
            ? deleteAllTubularsFromRun(currentChart?.rawEvent?.run_id)
            : deleteSingleEntity(TUBULAR_API, currentChart?.rawEvent?.id)
        ).then(() => {
            closeDeleteConfirmationModal()
            reloadData()
            setButtonController(false)
        })
    }

    function draw(): void {
        try {
            highchartsElement?.destroy()
            setChartLoaded(false)
            setHighchartsElement(
                Highcharts.chart(
                    chartRef?.current,
                    getActivityChartOptions(
                        { timeSpan: widgetTimeSpan, height: chartHeight },
                        widgetData.activitySeries,
                        theme,
                        openModal,
                        tryOpenModal,
                        setAddEventButtonVisible,
                        setTooltiPosition,
                        setActivityTooltipMessage,
                        checkBlockedTrackBeforeAction
                    ),
                    () => setChartLoaded(true)
                )
            )
        } catch (error) {
            // highchartsElement does not exits.
        }
    }

    useEffect(() => setChartHeight(chartRef.current.getBoundingClientRect().height), [])

    useEffect(() => draw(), [widgetData.activitySeries])

    useEffect(() => {
        if (chartLoaded) {
            setTimeout(() => {
                draw()
            }, 300)
        }
    }, [refresh, chartHeight, theme])

    const buttonProps = {
        buttonFunctions: [
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openDeleteConfirmationModal(currentChart?.rawEvent as RawActivityEvent)
                    setButtonController(false)
                }),
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openModal(currentChart?.rawEvent as RawActivityEvent)
                    setButtonController(false)
                })
        ],
        customIcons: [ButtonOptions.Delete, ButtonOptions.Confirm]
    }

    const [activityTooltipMessage, setActivityTooltipMessage] = useState(null)

    return (
        <>
            <ChartContainer>
                <StaticDataModal
                    isOpen={isModalOpen}
                    closeModal={closeModal}
                    isDouble={isModalDoubleSize}
                    content={
                        <ActivityModal
                            setIsModalDoubleSize={setIsModalDoubleSize}
                            isModalDoubleSize={isModalDoubleSize}
                            event={currentModalEvent}
                            closeModal={closeModal}
                        />
                    }
                />
                <ChartTitle>
                    <span>{i18n(StaticDataTypeI18nKey.ACTIVITY).toUpperCase()}</span>
                </ChartTitle>
                <Chart ref={chartRef} />
                {buttonController && buttonController === STATIC_DATA_STATES.EMPTY_DISABLED ? (
                    <div></div>
                ) : buttonController && buttonController !== STATIC_DATA_STATES.EMPTY ? (
                    chartType === 'tubularType' && renderButtons(buttonsRef, currentChart, buttonProps)
                ) : (
                    chartType === 'tubularType' &&
                    renderCreationTooltip(
                        buttonsRef,
                        currentChart,
                        tooltipPosition,
                        buttonController,
                        activityTooltipMessage
                    )
                )}
            </ChartContainer>
            <DeleteConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                closeModal={closeDeleteConfirmationModal}
                confirmDeletion={handleDelete}
                isActivityModal
                isTubularPerTime={!currentModalEvent?.finalMD}
            />
        </>
    )
}

const Chart = styled.div`
    height: 100%;
    width: 100%;
    max-height: 30px;
    min-height: 20px;
    margin-top: -16px;

    .highcharts-container {
        max-height: 30px;
        min-height: 20px;
        .highcharts-root {
            max-height: 30px;
            min-height: 20px;
            .highcharts-series-group {
                max-height: 30px;
                min-height: 20px;
                .highcharts-series highcharts-series-0 highcharts-xrange-series highcharts-tracker {
                    max-height: 30px;
                    min-height: 20px;
                    .highcharts-point {
                        max-height: 30px;
                        min-height: 20px;
                        .highcharts-point highcharts-partfill-original {
                            max-height: 30px;
                            min-height: 20px;
                        }
                    }
                }
            }
        }
    }
`
